import { UnitPrefix } from '@singular-health/models';
import { useAsyncState } from '@vueuse/core';
import { formatDate } from 'date-fns';
import { toValue, type MaybeRefOrGetter } from 'vue';
import { useMftpDirectoryService } from './useMftpDirectoryService';

export interface MftpTags {
  studyDate?: string;
  imageCount?: string;
  seriesDescription?: string;
  seriesNumber?: string;
  instanceId?: string;
}

function formatStudyDate(studyDate: string): string {
  if (/^\d{8}$/.test(studyDate)) {
    const year = studyDate.substring(0, 4);
    const month = studyDate.substring(4, 6);
    const day = studyDate.substring(6, 8);
    const date = new Date(Number(year), Number(month) - 1, Number(day));
    return formatDate(date, 'yyyy-MM-dd');
  }
  return studyDate;
}

function findTagValue(tags: Array<{ Key: string; Value: string }>, key: string): string | undefined {
  return tags.find((tag) => tag.Key === key)?.Value;
}

export function useMftpTags(
  collectionId: MaybeRefOrGetter<string>,
  tenantRegion?: MaybeRefOrGetter<string | null | undefined>,
) {
  const directoryService = useMftpDirectoryService();

  async function fetchMftpTags(): Promise<MftpTags> {
    const region = toValue(tenantRegion);
    const unitId = `${UnitPrefix.Collection}${toValue(collectionId)}`;
    const series = directoryService.getSelf(unitId, region);
    const study = directoryService.getParent(unitId, region);
    const [seriesDirectory, studyDirectory] = await Promise.all([series, study]);

    const seriesTags = seriesDirectory.UnitTags ?? [];
    const studyTags = studyDirectory.UnitTags ?? [];
    const allTags = [...seriesTags, ...studyTags];

    const rawStudyDate = findTagValue(allTags, 'study_StudyDate');

    return {
      studyDate: rawStudyDate ? formatStudyDate(rawStudyDate) : undefined,
      imageCount: findTagValue(allTags, 'series_sliceCount'),
      seriesDescription: findTagValue(allTags, 'series_description'),
      seriesNumber: findTagValue(allTags, 'series_number'),
      instanceId: findTagValue(allTags, 'study_InstanceId'),
    };
  }

  return useAsyncState(fetchMftpTags, null, { immediate: false });
}
